import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AtSign, Check, ExternalLink } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

export function useMentions() {
  return useQuery({ queryKey: ["mentions"], queryFn: () => api.get("/mentions").then((r) => r.data), refetchInterval: 30000 });
}

export function AttentionPanel({ onOpenTicket }) {
  const qc = useQueryClient();
  const { data } = useMentions();
  const items = data?.items || [];
  const markRead = useMutation({
    mutationFn: (ticket_oid) => api.post("/mentions/read", { ticket_oid }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mentions"] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const open = async (m) => {
    try {
      const { data: t } = await api.get(`/tickets/${m.ticket_oid}`);
      onOpenTicket(t);
      markRead.mutate(m.ticket_oid);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  if (!items.length) return null;

  return (
    <section className="rounded-2xl border-2 border-black bg-[#FACC15] p-4 brut-shadow fade-up sm:p-5" data-testid="attention-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold"><AtSign className="h-5 w-5" /> Needs your attention <span className="rounded-full bg-black px-2 py-0.5 text-sm text-white" data-testid="attention-count">{items.length}</span></h2>
        <button className="btn-ghost !py-1.5 !text-xs" onClick={() => markRead.mutate(null)} data-testid="attention-mark-all-read"><Check className="h-3.5 w-3.5" /> Mark all read</button>
      </div>
      <ul className="mt-3 space-y-2">
        {items.slice(0, 6).map((m) => (
          <li key={m.id} className="flex flex-col gap-2 rounded-xl border-2 border-black bg-white p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`mention-item-${m.id}`}>
            <div className="min-w-0 text-sm">
              <p><span className="font-mono font-bold">{m.ticket_id}</span> · <span className="font-semibold">{m.by}</span> mentioned you <span className="text-zinc-500">{fmtDate(m.created_at)}</span></p>
              <p className="truncate text-zinc-700">{m.text}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button className="btn-dark !px-3 !py-1.5 !text-xs" onClick={() => open(m)} data-testid={`mention-open-${m.id}`}><ExternalLink className="h-3.5 w-3.5" /> Open</button>
              <button className="btn-ghost !px-3 !py-1.5 !text-xs" onClick={() => markRead.mutate(m.ticket_oid)} data-testid={`mention-read-${m.id}`}><Check className="h-3.5 w-3.5" /></button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
